// Client-side helper that turns an API response into a browser download.
// Works for GET (just URL) and POST (sends body). Keeps consumers from
// reimplementing the blob / URL.createObjectURL dance every time.

export async function triggerDownload(
    url: string,
    filename: string,
    init?: RequestInit,
): Promise<void> {
    const res = await fetch(url, init ?? { method: 'GET' });
    if (!res.ok) {
        const text = await res.text().catch(() => '');
        throw new Error(`Download failed (${res.status}): ${text.slice(0, 200) || res.statusText}`);
    }

    const blob = await res.blob();
    downloadBlob(blob, filename);
}

/**
 * The file name that the server chose in the Content-Disposition header of a
 * response, or the fallback when the header names none.
 */
export function filenameFromResponse(res: Response, fallback: string): string {
    const header = res.headers.get('Content-Disposition') ?? '';
    const match = /filename="([^"]+)"/i.exec(header) ?? /filename=([^;]+)/i.exec(header);
    const name = match?.[1]?.trim();
    return name ? name : fallback;
}

/**
 * The encodings every `/api/export/*` tabular route can write. The server
 * serialises the SAME rows either way (`modules/export/tabular.ts`), so the
 * format is a download concern and not a domain one — which is why the type
 * lives here beside `triggerDownload` rather than in the UI kit.
 */
export type ExportFormat = 'csv' | 'xlsx';

/**
 * Downloads an `/api/export/*` tabular route in the chosen format.
 *
 * `csv` is the server default, so only `xlsx` adds the query parameter. The
 * path may already carry a query (the audit trail sends its filters), so the
 * separator is chosen rather than assumed.
 */
export function downloadTabularExport(
    path: string,
    baseName: string,
    format: ExportFormat,
): Promise<void> {
    const url = format === 'xlsx' ? `${path}${path.includes('?') ? '&' : '?'}format=xlsx` : path;
    return triggerDownload(url, `${baseName}.${format}`);
}

// Triggers a browser download from a Blob the caller already holds (e.g. a
// bespoke fetch that maps 404 → friendly message, or a client-side react-pdf
// render). Keeps the createObjectURL → anchor click → revoke dance in one place.
export function downloadBlob(blob: Blob, filename: string): void {
    const objectUrl = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = objectUrl;
    anchor.download = filename;
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
    // Release the blob shortly after click triggers the download.
    setTimeout(() => URL.revokeObjectURL(objectUrl), 1000);
}

// Turns a display name into a filename-safe slug (lower-case, dashes for runs
// of non-alphanumerics, no leading/trailing dashes).
export function slugifyName(name: string): string {
    return name
        .replace(/[^a-z0-9]+/gi, '-')
        .replace(/^-+|-+$/g, '')
        .toLowerCase();
}
